import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';

type RecurrenceFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

type TimeConfig = {
  messageType: string;
  messageContent: any;
};

type RecurrenceRule = {
  frequency: RecurrenceFrequency;
  daysOfWeek?: number[];
  times: string[];
  daysOfMonth?: number[];
  timeConfigs?: Record<string, TimeConfig>;
};

type TargetType = 'CONTACT_TAGS' | 'GROUPS';

type WhatsAppSession = {
  name: string;
  displayName?: string;
  mePushName: string | null;
  meId: string | null;
  provider?: string;
};

type ContactTag = {
  id: string;
  nome: string;
};

type RecurringJob = {
  id: string;
  nome: string;
  targetType: TargetType;
  targetTags: string[];
  targetGroups: string[];
  sessionNames: string[];
  messageType: string;
  messageContent: any;
  recurrence: RecurrenceRule;
  status: string;
  createdAt: string;
};

const daysOfWeekOptions = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' }
];

export function RecurringSendsPage() {
  const [jobs, setJobs] = useState<RecurringJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<RecurringJob | null>(null);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  
  // Time-specific configuration state
  const [configuringTime, setConfiguringTime] = useState<string | null>(null);
  const [timeConfigData, setTimeConfigData] = useState<{
    messageType: string;
    messageContent: any;
    fileInfo: { name: string; size: number; type: string } | null;
  }>({
    messageType: 'text',
    messageContent: { text: '' },
    fileInfo: null
  });
  
  // Group pagination and search states
  const [groupSearch, setGroupSearch] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const GROUPS_PER_PAGE = 20;

  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number; type: string } | null>(null);

  const [formData, setFormData] = useState({
    nome: '',
    targetType: 'CONTACT_TAGS' as TargetType,
    targetTags: [] as string[],
    targetGroupsText: '',
    sessionNames: [] as string[],
    messageType: 'text',
    messageContent: {
      text: ''
    } as any,
    recurrence: {
      frequency: 'WEEKLY' as RecurrenceFrequency,
      daysOfWeek: [1],
      times: ['09:00']
    } as RecurrenceRule
  });

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return fetch(url, {
      ...options,
      headers
    });
  };

  useEffect(() => {
    loadTags();
    loadSessions();
  }, []);

  useEffect(() => {
    loadJobs(page);
  }, [page]);

  const loadTags = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setTags(data || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setSessions(data || []);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const loadJobs = async (pageParam = 1) => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/campaigns/recurring?page=${pageParam}&limit=10`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar envios recorrentes:', error);
      toast.error('Erro ao carregar envios recorrentes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingJob(null);
    setFormData({
      nome: '',
      targetType: 'CONTACT_TAGS',
      targetTags: [],
      targetGroupsText: '',
      sessionNames: [],
      messageType: 'text',
      messageContent: {
        text: ''
      },
      recurrence: {
        frequency: 'WEEKLY',
        daysOfWeek: [1],
        times: ['09:00'],
        timeConfigs: {}
      }
    });
    setUploadingFile(false);
    setFileInfo(null);
    setConfiguringTime(null);
    setTimeConfigData({
      messageType: 'text',
      messageContent: { text: '' },
      fileInfo: null
    });
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditJob = (job: RecurringJob) => {
    setEditingJob(job);
    setFormData({
      nome: job.nome,
      targetType: job.targetType,
      targetTags: job.targetType === 'CONTACT_TAGS' ? job.targetTags || [] : [],
      targetGroupsText: job.targetType === 'GROUPS' ? (job.targetGroups || []).join('\n') : '',
      sessionNames: job.sessionNames || [],
      messageType: job.messageType || 'text',
      messageContent: job.messageContent || {
        text: ''
      },
      recurrence: job.recurrence ? {
        ...job.recurrence,
        timeConfigs: job.recurrence.timeConfigs || {}
      } : {
        frequency: 'WEEKLY',
        daysOfWeek: [1],
        times: ['09:00'],
        timeConfigs: {}
      }
    });
    if (job.messageType !== 'text' && job.messageContent && job.messageContent.url) {
      setFileInfo({
        name: job.messageContent.fileName || 'arquivo',
        size: job.messageContent.size || 0,
        type: job.messageContent.mimetype || 'application/octet-stream'
      });
    } else {
      setFileInfo(null);
    }
    setUploadingFile(false);
    setShowModal(true);
  };

  const handleFileUpload = async (file: File) => {
    setUploadingFile(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      setFileInfo({
        name: data.originalName,
        size: data.size,
        type: data.mimetype
      });

      setFormData(prev => ({
        ...prev,
        messageContent: {
          ...prev.messageContent,
          url: data.fileUrl,
          fileName: data.originalName
        }
      }));

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      messageContent: {
        ...prev.messageContent,
        url: '',
        fileName: undefined
      }
    }));
    setFileInfo(null);
    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const handleToggleTag = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagId)
        ? prev.targetTags.filter(t => t !== tagId)
        : [...prev.targetTags, tagId]
    }));
  };

  const handleSelectAllTags = () => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.length === tags.length ? [] : tags.map(t => t.id)
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => {
      if (prev.targetType === 'GROUPS') {
        return {
          ...prev,
          sessionNames: prev.sessionNames.includes(sessionName) ? [] : [sessionName]
        };
      }
      return {
        ...prev,
        sessionNames: prev.sessionNames.includes(sessionName)
          ? prev.sessionNames.filter(s => s !== sessionName)
          : [...prev.sessionNames, sessionName]
      };
    });
  };

  const handleAddTime = () => {
    setFormData(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        times: [...(prev.recurrence.times || []), '09:00']
      }
    }));
  };

  const handleTimeChange = (index: number, value: string) => {
    const normalized = value.slice(0, 5);
    setFormData(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        times: prev.recurrence.times.map((t, i) => (i === index ? normalized : t))
      }
    }));
  };

  const handleRemoveTime = (index: number) => {
    setFormData(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        times: prev.recurrence.times.filter((_, i) => i !== index)
      }
    }));
  };

  const handleToggleDayOfWeek = (value: number) => {
    setFormData(prev => {
      const current = prev.recurrence.daysOfWeek || [];
      const exists = current.includes(value);
      const next = exists ? current.filter(d => d !== value) : [...current, value];
      return {
        ...prev,
        recurrence: {
          ...prev.recurrence,
          daysOfWeek: next.length === 0 ? [] : next
        }
      };
    });
  };

  const handleOpenTimeConfig = (time: string) => {
    const config = formData.recurrence.timeConfigs?.[time];
    if (config) {
      setTimeConfigData({
        messageType: config.messageType,
        messageContent: config.messageContent,
        fileInfo: config.messageType !== 'text' && config.messageContent.url ? {
          name: config.messageContent.fileName || 'arquivo',
          size: config.messageContent.size || 0,
          type: config.messageContent.mimetype || 'application/octet-stream'
        } : null
      });
    } else {
      // Use default message as starting point
      setTimeConfigData({
        messageType: formData.messageType,
        messageContent: formData.messageContent,
        fileInfo: fileInfo
      });
    }
    setConfiguringTime(time);
  };

  const handleSaveTimeConfig = () => {
    if (!configuringTime) return;
    
    setFormData(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        timeConfigs: {
          ...(prev.recurrence.timeConfigs || {}),
          [configuringTime]: {
            messageType: timeConfigData.messageType,
            messageContent: timeConfigData.messageContent
          }
        }
      }
    }));
    setConfiguringTime(null);
    toast.success(`Mensagem específica para ${configuringTime} configurada`);
  };

  const handleRemoveTimeConfig = (time: string) => {
    setFormData(prev => {
      const newConfigs = { ...(prev.recurrence.timeConfigs || {}) };
      delete newConfigs[time];
      return {
        ...prev,
        recurrence: {
          ...prev.recurrence,
          timeConfigs: newConfigs
        }
      };
    });
    toast.success(`Mensagem específica para ${time} removida`);
  };

  const handleTimeFileUpload = async (file: File) => {
    setUploadingFile(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      setTimeConfigData(prev => ({
        ...prev,
        fileInfo: {
          name: data.originalName,
          size: data.size,
          type: data.mimetype
        },
        messageContent: {
          ...prev.messageContent,
          url: data.fileUrl,
          fileName: data.originalName
        }
      }));

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveTimeFile = () => {
    setTimeConfigData(prev => ({
      ...prev,
      messageContent: {
        ...prev.messageContent,
        url: '',
        fileName: undefined
      },
      fileInfo: null
    }));
    toast.success('Arquivo removido');
  };

  const handleLoadGroups = async () => {
    if (formData.sessionNames.length !== 1) {
      toast.error('Selecione uma única conexão para sincronizar os grupos');
      return;
    }
    const sessionName = formData.sessionNames[0];
    try {
      setLoadingGroups(true);
      const response = await authenticatedFetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/groups`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const sessionGroups: Array<{ id: string; name: string }> = (data.groups || []).map((g: any) => ({
        id: g.id || g.jid || g.chatId || g.chat_id || g.wid || '',
        name: g.name || g.subject || g.title || g.id || g.jid || ''
      })).filter(g => g.id && g.name);
      setGroups(sessionGroups);
      setGroupPage(1); // Reset page on reload
      if (!sessionGroups.length) {
        toast('Nenhum grupo encontrado para esta conexão');
      } else {
        toast.success(`Carregados ${sessionGroups.length} grupos`);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos da sessão:', error);
      toast.error('Erro ao carregar grupos da sessão');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleToggleGroup = (groupId: string) => {
    setFormData(prev => {
      const lines = prev.targetGroupsText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      const exists = lines.includes(groupId);
      const next = exists ? lines.filter(id => id !== groupId) : [...lines, groupId];
      return {
        ...prev,
        targetGroupsText: next.join('\n')
      };
    });
  };
  
  // Filtered and paginated groups
  const filteredGroups = groups.filter(g => 
    g.name.toLowerCase().includes(groupSearch.toLowerCase()) || 
    g.id.toLowerCase().includes(groupSearch.toLowerCase())
  );
  
  const totalGroupPages = Math.ceil(filteredGroups.length / GROUPS_PER_PAGE);
  const paginatedGroups = filteredGroups.slice(
    (groupPage - 1) * GROUPS_PER_PAGE,
    groupPage * GROUPS_PER_PAGE
  );

  const handleSelectAllFilteredGroups = () => {
    const newGroups = filteredGroups.map(g => g.id);
    setFormData(prev => {
      const current = prev.targetGroupsText.split('\n').map(s => s.trim()).filter(Boolean);
      // Merge current with new (avoid duplicates)
      const unique = Array.from(new Set([...current, ...newGroups]));
      return {
        ...prev,
        targetGroupsText: unique.join('\n')
      };
    });
    toast.success(`${newGroups.length} grupos selecionados`);
  };

  const buildPayload = () => {
    const targetGroups = formData.targetType === 'GROUPS'
      ? formData.targetGroupsText
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
      : [];
    const recurrence: RecurrenceRule = {
      frequency: formData.recurrence.frequency,
      times: formData.recurrence.times.filter(Boolean)
    };
    if (recurrence.frequency === 'WEEKLY') {
      recurrence.daysOfWeek = (formData.recurrence.daysOfWeek || []).length
        ? formData.recurrence.daysOfWeek
        : undefined;
    }
    return {
      nome: formData.nome,
      targetType: formData.targetType,
      targetTags: formData.targetType === 'CONTACT_TAGS' ? formData.targetTags : [],
      targetGroups: formData.targetType === 'GROUPS' ? targetGroups : [],
      sessionNames: formData.sessionNames,
      messageType: formData.messageType,
      messageContent: formData.messageContent,
      recurrence
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('Informe o nome do envio recorrente');
      return;
    }
    if (formData.targetType === 'CONTACT_TAGS' && formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contatos');
      return;
    }
    if (formData.targetType === 'GROUPS' && !formData.targetGroupsText.trim()) {
      toast.error('Informe pelo menos um grupo');
      return;
    }
    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }
    if (!formData.recurrence.times.length) {
      toast.error('Adicione pelo menos um horário');
      return;
    }
    if (formData.messageType === 'text') {
      if (!formData.messageContent?.text || !formData.messageContent.text.trim()) {
        toast.error('Informe o conteúdo de texto da mensagem');
        return;
      }
    } else {
      if (!formData.messageContent || !formData.messageContent.url) {
        toast.error('Selecione um arquivo para envio com anexo');
        return;
      }
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      let response;
      if (editingJob) {
        response = await authenticatedFetch(`/api/campaigns/recurring/${editingJob.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await authenticatedFetch('/api/campaigns/recurring', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.error || 'Erro ao salvar envio recorrente';
        throw new Error(message);
      }
      toast.success('Envio recorrente salvo com sucesso');
      setShowModal(false);
      resetForm();
      loadJobs(page);
    } catch (error: any) {
      console.error('Erro ao salvar envio recorrente:', error);
      toast.error(error.message || 'Erro ao salvar envio recorrente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (job: RecurringJob) => {
    const confirmed = window.confirm(`Excluir envio recorrente "${job.nome}"?`);
    if (!confirmed) return;
    try {
      const response = await authenticatedFetch(`/api/campaigns/recurring/${job.id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.error || 'Erro ao excluir envio recorrente';
        throw new Error(message);
      }
      toast.success('Envio recorrente excluído com sucesso');
      loadJobs(page);
    } catch (error: any) {
      console.error('Erro ao excluir envio recorrente:', error);
      toast.error(error.message || 'Erro ao excluir envio recorrente');
    }
  };

  const handleToggleStatus = async (job: RecurringJob) => {
    try {
      const action = job.status === 'PAUSED' ? 'resume' : 'pause';
      const response = await authenticatedFetch(`/api/campaigns/recurring/${job.id}/toggle`, {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.error || 'Erro ao alterar status';
        throw new Error(message);
      }
      loadJobs(page);
    } catch (error: any) {
      console.error('Erro ao alterar status do envio recorrente:', error);
      toast.error(error.message || 'Erro ao alterar status');
    }
  };

  const renderRecurrenceSummary = (recurrence: RecurrenceRule | undefined) => {
    if (!recurrence) return 'Não configurado';
    const times = recurrence.times.join(', ');
    if (recurrence.frequency === 'DAILY') {
      return `Diariamente às ${times}`;
    }
    if (recurrence.frequency === 'WEEKLY') {
      const days = (recurrence.daysOfWeek || [])
        .map(d => daysOfWeekOptions.find(o => o.value === d)?.label)
        .filter(Boolean)
        .join(', ');
      return `Semanalmente (${days}) às ${times}`;
    }
    if (recurrence.frequency === 'MONTHLY') {
      return `Mensalmente às ${times}`;
    }
    return times;
  };

  if (loading) {
    return (
      <>
        <Header
          title="Envios Recorrentes"
          subtitle="Gerencie seus envios automáticos por horários e dias"
        />
        <div className="p-6">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-2 text-gray-600">Carregando envios recorrentes...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        title="Envios Recorrentes"
        subtitle={`${total} regras configuradas`}
        actions={
          <button onClick={handleOpenModal} className="btn-primary">
            + Novo Envio Recorrente
          </button>
        }
      />

      <div className="p-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Regras de Envios Recorrentes</h3>
            <span className="text-sm text-gray-500">
              Total: {total}
            </span>
          </div>
          {jobs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhum envio recorrente configurado. Crie sua primeira regra para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {jobs.map(job => (
                <div key={job.id} className="px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{job.nome}</span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            job.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {job.status === 'ACTIVE' ? 'Ativo' : 'Pausado'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {job.targetType === 'CONTACT_TAGS'
                          ? `Contatos por categorias: ${
                              job.targetTags
                                .map(id => tags.find(t => t.id === id)?.nome || id)
                                .join(', ') || 'Nenhuma'
                            }`
                          : `Grupos: ${job.targetGroups.join(', ')}`}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {renderRecurrenceSummary(job.recurrence)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleStatus(job)}
                      className={`px-2 py-1 text-xs rounded ${
                        job.status === 'ACTIVE'
                          ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {job.status === 'ACTIVE' ? 'Pausar' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => handleEditJob(job)}
                      className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(job)}
                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Mostrando página {page} de {totalPages} • Total: {total}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-2 text-sm rounded-md ${
                            pageNum === page
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                      disabled={page === totalPages}
                      className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingJob ? 'Editar Envio Recorrente' : 'Novo Envio Recorrente'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Configure horários, dias e destinos para seus envios automáticos.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Nome do envio recorrente</label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="mt-1 input-field"
                        placeholder="Ex: Alertas diários para grupos"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de destino</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, targetType: 'CONTACT_TAGS' }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                            formData.targetType === 'CONTACT_TAGS'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 text-gray-700'
                          }`}
                        >
                          Contatos por categorias
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, targetType: 'GROUPS' }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                            formData.targetType === 'GROUPS'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 text-gray-700'
                          }`}
                        >
                          Grupos
                        </button>
                      </div>
                    </div>

                    {formData.targetType === 'CONTACT_TAGS' ? (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">Categorias de contatos</label>
                          <button
                            type="button"
                            onClick={handleSelectAllTags}
                            className="text-xs text-green-600 hover:text-green-800"
                          >
                            {formData.targetTags.length === tags.length ? 'Limpar seleção' : 'Selecionar todas'}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {tags.map(tag => {
                            const selected = formData.targetTags.includes(tag.id);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => handleToggleTag(tag.id)}
                                className={`px-3 py-1 rounded-full text-xs border ${
                                  selected
                                    ? 'bg-green-600 text-white border-green-600'
                                    : 'bg-gray-100 text-gray-700 border-gray-300'
                                }`}
                              >
                                {tag.nome}
                              </button>
                            );
                          })}
                          {tags.length === 0 && (
                            <span className="text-xs text-gray-500">
                              Nenhuma categoria cadastrada.
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Selecione os Grupos</label>
                          <button
                            type="button"
                            onClick={handleLoadGroups}
                            disabled={loadingGroups || formData.sessionNames.length !== 1}
                            className="text-xs text-green-600 hover:text-green-800 disabled:opacity-50"
                          >
                            {loadingGroups ? 'Carregando...' : 'Sincronizar grupos da conexão'}
                          </button>
                        </div>
                        
                        {groups.length > 0 ? (
                          <div className="space-y-2">
                            {/* Search and Filter Controls */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Buscar grupos..."
                                value={groupSearch}
                                onChange={(e) => {
                                  setGroupSearch(e.target.value);
                                  setGroupPage(1);
                                }}
                                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                              />
                              <button
                                type="button"
                                onClick={handleSelectAllFilteredGroups}
                                className="px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 whitespace-nowrap"
                              >
                                Selecionar Visíveis
                              </button>
                            </div>

                            {/* Group List */}
                            <div className="border border-gray-300 rounded-md p-2 bg-gray-50 max-h-60 overflow-y-auto">
                              {paginatedGroups.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-2">Nenhum grupo encontrado</p>
                              ) : (
                                <div className="space-y-1">
                                  {paginatedGroups.map(group => {
                                    const isSelected = formData.targetGroupsText
                                      .split('\n')
                                      .map(s => s.trim())
                                      .includes(group.id);
                                    
                                    return (
                                      <button
                                        key={group.id}
                                        type="button"
                                        onClick={() => handleToggleGroup(group.id)}
                                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                                          isSelected
                                            ? 'bg-green-100 text-green-800 font-medium'
                                            : 'hover:bg-gray-200 text-gray-700'
                                        }`}
                                      >
                                        <span className="truncate flex-1" title={group.name}>{group.name}</span>
                                        {isSelected && <span>✓</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Pagination Controls */}
                            {totalGroupPages > 1 && (
                              <div className="flex items-center justify-between text-xs text-gray-600">
                                <button
                                  type="button"
                                  onClick={() => setGroupPage(p => Math.max(1, p - 1))}
                                  disabled={groupPage === 1}
                                  className="px-2 py-1 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200"
                                >
                                  Anterior
                                </button>
                                <span>
                                  Página {groupPage} de {totalGroupPages} ({filteredGroups.length} grupos)
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setGroupPage(p => Math.min(totalGroupPages, p + 1))}
                                  disabled={groupPage === totalGroupPages}
                                  className="px-2 py-1 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200"
                                >
                                  Próxima
                                </button>
                              </div>
                            )}
                            
                            <div className="text-xs text-gray-500 text-right">
                              Total carregado: {groups.length} grupos
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 border border-dashed border-gray-300 rounded p-4 text-center">
                            Nenhum grupo sincronizado. Selecione uma conexão e clique em &quot;Sincronizar grupos&quot;.
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Conexões WhatsApp</label>
                      <div className="flex flex-wrap gap-2">
                        {sessions.map(session => {
                          const selected = formData.sessionNames.includes(session.name);
                          const label = session.displayName || session.mePushName || session.name;
                          return (
                            <button
                              key={session.name}
                              type="button"
                              onClick={() => handleSessionToggle(session.name)}
                              className={`px-3 py-1 rounded-full text-xs border ${
                                selected
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'bg-gray-100 text-gray-700 border-gray-300'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                        {sessions.length === 0 && (
                          <span className="text-xs text-gray-500">
                            Nenhuma conexão ativa encontrada.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Frequência</label>
                      <select
                        value={formData.recurrence.frequency}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            recurrence: {
                              ...prev.recurrence,
                              frequency: e.target.value as RecurrenceFrequency
                            }
                          }))
                        }
                        className="input-field mt-1"
                      >
                        <option value="DAILY">Diariamente</option>
                        <option value="WEEKLY">Semanalmente</option>
                        <option value="MONTHLY">Mensalmente</option>
                      </select>
                    </div>

                    {formData.recurrence.frequency === 'WEEKLY' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dias da semana
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {daysOfWeekOptions.map(day => {
                            const selected = (formData.recurrence.daysOfWeek || []).includes(day.value);
                            return (
                              <button
                                key={day.value}
                                type="button"
                                onClick={() => handleToggleDayOfWeek(day.value)}
                                className={`px-2 py-1 rounded-full text-xs border ${
                                  selected
                                    ? 'bg-green-600 text-white border-green-600'
                                    : 'bg-gray-100 text-gray-700 border-gray-300'
                                }`}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Horários de envio
                        </label>
                        <button
                          type="button"
                          onClick={handleAddTime}
                          className="text-xs text-green-600 hover:text-green-800"
                        >
                          Adicionar horário
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.recurrence.times.map((time, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="time"
                              value={time}
                              onChange={e => handleTimeChange(index, e.target.value)}
                              className="input-field w-32"
                            />
                            <button
                              type="button"
                              onClick={() => handleOpenTimeConfig(time)}
                              className={`px-2 py-1 text-xs rounded border ${
                                formData.recurrence.timeConfigs?.[time]
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              {formData.recurrence.timeConfigs?.[time] ? 'Msg Personalizada' : 'Personalizar Msg'}
                            </button>
                            {formData.recurrence.timeConfigs?.[time] && (
                              <button
                                type="button"
                                onClick={() => handleRemoveTimeConfig(time)}
                                className="text-xs text-red-500 hover:text-red-700 font-bold px-1"
                                title="Remover configuração específica e usar padrão"
                              >
                                ×
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveTime(index)}
                              className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 ml-auto"
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                        {formData.recurrence.times.length === 0 && (
                          <div className="text-xs text-gray-500">
                            Nenhum horário adicionado. Adicione pelo menos um horário.
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo e conteúdo da mensagem
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Configure se a mensagem será apenas texto ou terá um anexo.
                      </p>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {[
                            { type: 'text', label: 'Texto' },
                            { type: 'image', label: 'Imagem' },
                            { type: 'video', label: 'Vídeo' },
                            { type: 'audio', label: 'Áudio' },
                            { type: 'document', label: 'Arquivo' }
                          ].map(option => (
                            <button
                              key={option.type}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  messageType: option.type,
                                  messageContent:
                                    option.type === 'text'
                                      ? { text: prev.messageContent?.text || '' }
                                      : {
                                          url: prev.messageContent?.url || '',
                                          caption: prev.messageContent?.caption || '',
                                          fileName: prev.messageContent?.fileName
                                        }
                                }));
                              }}
                              className={`px-3 py-1 rounded-full text-xs border ${
                                formData.messageType === option.type
                                  ? 'bg-green-600 text-white border-green-600'
                                  : 'bg-gray-100 text-gray-700 border-gray-300'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>

                        {formData.messageType === 'text' && (
                          <textarea
                            value={formData.messageContent?.text || ''}
                            onChange={e =>
                              setFormData(prev => ({
                                ...prev,
                                messageContent: {
                                  text: e.target.value
                                }
                              }))
                            }
                            className="mt-1 input-field"
                            rows={6}
                            placeholder="Digite aqui a mensagem que será enviada..."
                          />
                        )}

                        {formData.messageType !== 'text' && (
                          <div className="space-y-3">
                            <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                              {!formData.messageContent?.url ? (
                                <>
                                  <input
                                    type="file"
                                    id="recurring-file-upload"
                                    className="hidden"
                                    accept={
                                      formData.messageType === 'image'
                                        ? 'image/*'
                                        : formData.messageType === 'video'
                                        ? 'video/*'
                                        : formData.messageType === 'audio'
                                        ? 'audio/*'
                                        : 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                    }
                                    onChange={e => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleFileUpload(file);
                                      }
                                    }}
                                    disabled={uploadingFile}
                                  />
                                  <label
                                    htmlFor="recurring-file-upload"
                                    className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-green-400 hover:bg-green-50 ${
                                      uploadingFile
                                        ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                        : 'text-gray-500 hover:text-green-600'
                                    }`}
                                  >
                                    {uploadingFile ? (
                                      <>
                                        <svg
                                          className="animate-spin h-5 w-5 text-green-600"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8H4z"
                                          ></path>
                                        </svg>
                                        <span className="mt-2 text-sm">Carregando...</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg
                                          className="w-10 h-10 text-gray-400 mb-2"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v6m-3-3h6"
                                          />
                                        </svg>
                                        <span className="text-sm">Clique para fazer upload do arquivo</span>
                                      </>
                                    )}
                                  </label>
                                </>
                              ) : (
                                <div className="p-3 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {formData.messageType === 'image' && (
                                      <img
                                        src={formData.messageContent.url}
                                        alt="Preview"
                                        className="w-12 h-12 object-cover rounded"
                                      />
                                    )}
                                    <div className="flex flex-col">
                                      <span className="text-sm font-medium text-gray-700">
                                        {fileInfo?.name || formData.messageContent.fileName || 'arquivo'}
                                      </span>
                                      {fileInfo && (
                                        <span className="text-xs text-gray-500">
                                          {formatFileSize(fileInfo.size)} • {fileInfo.type}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    className="text-xs text-red-600 hover:text-red-800"
                                  >
                                    Remover
                                  </button>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Legenda (opcional)
                              </label>
                              <textarea
                                value={formData.messageContent?.caption || ''}
                                onChange={e =>
                                  setFormData(prev => ({
                                    ...prev,
                                    messageContent: {
                                      ...prev.messageContent,
                                      caption: e.target.value
                                    }
                                  }))
                                }
                                className="input-field"
                                rows={3}
                                placeholder="Digite uma legenda para o anexo (se desejar)..."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-3 border-t pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? 'Salvando...' : 'Salvar envio recorrente'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {configuringTime && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 10000 }}>
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Configurar Mensagem para {configuringTime}
                  </h3>
                  <button
                    onClick={() => setConfiguringTime(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-500">
                    Defina uma mensagem específica para este horário. Se remover esta configuração, será usada a mensagem padrão.
                  </p>

                  {/* Message Type Selector */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { type: 'text', label: 'Texto' },
                      { type: 'image', label: 'Imagem' },
                      { type: 'video', label: 'Vídeo' },
                      { type: 'audio', label: 'Áudio' },
                      { type: 'document', label: 'Arquivo' }
                    ].map(option => (
                      <button
                        key={option.type}
                        type="button"
                        onClick={() => {
                          setTimeConfigData(prev => ({
                            ...prev,
                            messageType: option.type,
                            messageContent:
                              option.type === 'text'
                                ? { text: prev.messageContent?.text || '' }
                                : {
                                    url: prev.messageContent?.url || '',
                                    caption: prev.messageContent?.caption || '',
                                    fileName: prev.messageContent?.fileName
                                  }
                          }));
                        }}
                        className={`px-3 py-1 rounded-full text-xs border ${
                          timeConfigData.messageType === option.type
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Text Content */}
                  {timeConfigData.messageType === 'text' && (
                    <textarea
                      value={timeConfigData.messageContent?.text || ''}
                      onChange={e =>
                        setTimeConfigData(prev => ({
                          ...prev,
                          messageContent: {
                            text: e.target.value
                          }
                        }))
                      }
                      className="mt-1 input-field w-full"
                      rows={6}
                      placeholder="Digite aqui a mensagem específica para este horário..."
                    />
                  )}

                  {/* File Upload Content */}
                  {timeConfigData.messageType !== 'text' && (
                    <div className="space-y-3">
                      <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 max-h-64">
                        {!timeConfigData.messageContent?.url ? (
                          <>
                            <input
                              type="file"
                              id="time-config-file-upload"
                              className="hidden"
                              accept={
                                timeConfigData.messageType === 'image'
                                  ? 'image/*'
                                  : timeConfigData.messageType === 'video'
                                  ? 'video/*'
                                  : timeConfigData.messageType === 'audio'
                                  ? 'audio/*'
                                  : 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                              }
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleTimeFileUpload(file);
                                }
                              }}
                              disabled={uploadingFile}
                            />
                            <label
                              htmlFor="time-config-file-upload"
                              className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-green-400 hover:bg-green-50 ${
                                uploadingFile
                                  ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                  : 'text-gray-500 hover:text-green-600'
                              }`}
                            >
                              {uploadingFile ? (
                                <span className="text-sm">Carregando...</span>
                              ) : (
                                <div className="text-center p-4">
                                  <span className="text-sm block">Clique para fazer upload</span>
                                </div>
                              )}
                            </label>
                          </>
                        ) : (
                          <div className="p-3 flex items-center justify-between h-full">
                            <div className="flex items-center gap-3">
                              {timeConfigData.messageType === 'image' && (
                                <img
                                  src={timeConfigData.messageContent.url}
                                  alt="Preview"
                                  className="w-16 h-16 object-cover rounded"
                                />
                              )}
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                                  {timeConfigData.fileInfo?.name || timeConfigData.messageContent.fileName || 'arquivo'}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveTimeFile}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remover
                            </button>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Legenda (opcional)
                        </label>
                        <textarea
                          value={timeConfigData.messageContent?.caption || ''}
                          onChange={e =>
                            setTimeConfigData(prev => ({
                              ...prev,
                              messageContent: {
                                ...prev.messageContent,
                                caption: e.target.value
                              }
                            }))
                          }
                          className="input-field w-full"
                          rows={3}
                          placeholder="Legenda para este horário..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setConfiguringTime(null)}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTimeConfig}
                    className="btn-primary"
                  >
                    Confirmar Personalização
                  </button>
                </div>
              </div>
            </div>
          )}
        </Portal>
      )}
    </>
  );
}
